import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Calendar, User, Briefcase, Building2, BookOpen, Loader2, GraduationCap, Award, CreditCard, Contact, Home, Heart, Banknote } from 'lucide-react';
import {
  getStaffProfileWithTenant,
  getStaffRolePermissionsWithTenant,
  type StaffUser,
  type ProfessorUser,
} from '@/services/superAdminUserService';
import { formatPhoneWithCountry } from '@/shared/constants/phoneCodes';
import { countryDisplayName } from '@/shared/constants/countryCode';
import { handicapLabel } from '@/shared/constants/handicap';
import { emergencyRelationLabel } from '@/shared/constants/emergencyRelation';

// Country code to full name mapping (French)
interface ViewStaffModalSuperAdminProps {
  isOpen: boolean;
  onClose: () => void;
  staff: StaffUser | ProfessorUser | null;
  tenantId: string;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-800' },
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  INACTIVE: { label: 'Inactif', color: 'bg-gray-100 text-gray-800' },
  SUSPENDED: { label: 'Suspendu', color: 'bg-red-100 text-red-800' },
  WITHDRAWN: { label: 'Archive', color: 'bg-orange-100 text-orange-800' },
};

const roleLabels: Record<string, string> = {
  DIRECTOR_GENERAL: 'Directeur General',
  BRANCH_MANAGER: 'Directeur de Campus',
  PEDAGOGICAL_DIRECTOR: 'Directeur Pedagogique',
  FINANCIAL_MANAGER: 'Directeur Financier',
  MARKETING_MANAGER: 'Directeur Marketing',
  COMMERCIAL_MANAGER: 'Directeur Commercial',
  PROGRAM_HEAD: 'Chef de Filiere',
  ACADEMIC_COORDINATOR: 'Coordinateur Academique',
  TEACHER: 'Professeur',
};

const statutProfesseurLabels: Record<string, string> = {
  P: 'Permanent',
  V: 'Vacataire',
};

const formatPhoneDisplay = (phone?: string, countryCode?: string): string => {
  const formatted = formatPhoneWithCountry(phone, countryCode);
  return formatted || '-';
};

const InfoItem: React.FC<{
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  iconColor?: string;
}> = ({ icon: Icon, label, value, iconColor = 'text-gray-400' }) => (
  <div className="flex items-start gap-3">
    <Icon size={18} className={`${iconColor} mt-0.5`} />
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{value || '-'}</p>
    </div>
  </div>
);

export const ViewStaffModalSuperAdmin: React.FC<ViewStaffModalSuperAdminProps> = ({
  isOpen,
  onClose,
  staff,
  tenantId,
}) => {
  const [rolePermissions, setRolePermissions] = useState<any>(null);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [fullProfile, setFullProfile] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    if (isOpen && staff && tenantId) {
      loadRolePermissions();
      loadFullProfile();
    }
    if (!isOpen) {
      setRolePermissions(null);
      setFullProfile(null);
      setIsLoadingPermissions(false);
      setIsLoadingProfile(false);
    }
  }, [isOpen, staff?.id, tenantId]);

  const loadFullProfile = async () => {
    if (!staff || !tenantId) return;
    setIsLoadingProfile(true);
    try {
      const profile = await getStaffProfileWithTenant(staff.id, tenantId);
      setFullProfile(profile);
    } catch (error) {
      console.error('Error loading full profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const loadRolePermissions = async () => {
    if (!staff || !tenantId) return;
    setIsLoadingPermissions(true);
    try {
      const perms = await getStaffRolePermissionsWithTenant(staff.id, tenantId);
      setRolePermissions(perms);
    } catch (error) {
      console.error('Error loading role permissions:', error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  if (!isOpen || !staff) return null;

  const getRoleName = (role: string): string => {
    if (rolePermissions?.currentRole === role && rolePermissions.currentRoleInfo) {
      return rolePermissions.currentRoleInfo.nameFr;
    }
    return roleLabels[role] || role;
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const profile = fullProfile?.profile;
  const isProfessorOrAlso = staff.role === 'TEACHER' || (staff as ProfessorUser).isAlsoProfessor || fullProfile?.isAlsoProfessor;

  const getGradeLabel = (gradeId?: string): string => {
    if (!gradeId || !fullProfile?.nomenclature?.grades) return '-';
    const grade = fullProfile.nomenclature.grades.find((g: any) => g.id === gradeId);
    return grade?.labelFr || '-';
  };

  const getDepartmentLabel = (deptId?: string): string => {
    if (!deptId || !fullProfile?.nomenclature?.departments) return '-';
    const dept = fullProfile.nomenclature.departments.find((d: any) => d.id === deptId);
    return dept?.labelFr || '-';
  };

  const getDiplomeLabel = (diplomeId?: string): string => {
    if (!diplomeId || !fullProfile?.nomenclature?.diplomes) return '-';
    const diplome = fullProfile.nomenclature.diplomes.find((d: any) => d.id === diplomeId);
    return diplome?.labelFr || '-';
  };

  const getPayrollCampusName = (): string => {
    if (!fullProfile?.payrollCampusId || !fullProfile?.campuses) return '-';
    const campus = fullProfile.campuses.find((c: any) => c.id === fullProfile.payrollCampusId);
    return campus?.name || '-';
  };

  const getCountryName = (code?: string): string => countryDisplayName(code);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl">
            <h2 className="text-xl font-semibold text-gray-900">Details du membre</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Profile Header */}
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-semibold text-xl">
                  {staff.firstName[0]}{staff.lastName[0]}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {staff.firstName} {staff.lastName}
                </h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {getRoleName(staff.role)}
                  </span>
                  {staff.staffCode && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 font-mono">
                      {staff.staffCode}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabels[staff.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                    {statusLabels[staff.status]?.label || staff.status}
                  </span>
                  {staff.academicYear && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Calendar size={12} />
                      {staff.academicYear.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {(isLoadingProfile || isLoadingPermissions) ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-primary-600" size={24} />
                <span className="ml-2 text-sm text-gray-500">Chargement des details...</span>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Section 1: Contact */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <User size={16} className="text-gray-400" />
                    Contact
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={Mail} label="Email" value={staff.email} />
                    <InfoItem icon={Phone} label="Telephone" value={formatPhoneDisplay(staff.phone || undefined, staff.phoneCountry || undefined)} />
                  </div>
                </div>

                {/* Section 2: Affiliation */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Building2 size={16} className="text-gray-400" />
                    Affiliation
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <MapPin size={18} className="text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Site(s) d'affectation</p>
                        {staff.campuses && staff.campuses.length > 0 ? (
                          <div className="space-y-1 mt-1">
                            {staff.campuses.map((campus) => (
                              <div key={campus.id} className="flex items-center gap-2">
                                <span className="text-sm text-gray-900">{campus.name}</span>
                                {campus.isPrimary && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                    Principal
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">Tous les sites (acces global)</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar size={18} className="text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Annee academique</p>
                        {staff.academicYear ? (
                          <span className="text-sm text-gray-900">{staff.academicYear.name}</span>
                        ) : (
                          <p className="text-sm text-gray-900">-</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Identite */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Contact size={16} className="text-gray-400" />
                    Identite
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={Contact} label="CIN" value={profile?.cnie} />
                    <InfoItem icon={Contact} label="Passeport" value={profile?.passport} />
                    <InfoItem icon={MapPin} label="Nationalite" value={getCountryName(profile?.paysOrigine)} />
                    <InfoItem icon={MapPin} label="Situation de handicap" value={handicapLabel(profile?.handicap)} />
                    <InfoItem icon={Calendar} label="Date de naissance" value={formatDate(profile?.dateNaissance)} />
                    <InfoItem
                      icon={User}
                      label="Genre"
                      value={profile?.sexe === 'M' ? 'Masculin' : profile?.sexe === 'F' ? 'Feminin' : profile?.sexe || '-'}
                    />
                  </div>
                </div>

                {/* Section 4: Adresse */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Home size={16} className="text-gray-400" />
                    Adresse
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <InfoItem icon={Home} label="Adresse" value={profile?.adresse} />
                  </div>
                </div>

                {/* Section 5: Informations Professionnelles */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Briefcase size={16} className="text-gray-400" />
                    Informations Professionnelles
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={Briefcase} label="Fonction" value={getRoleName(staff.role)} />
                    <InfoItem icon={Calendar} label="Date de recrutement" value={formatDate(profile?.dateRecrutement)} />
                    <InfoItem icon={GraduationCap} label="Diplome" value={getDiplomeLabel(profile?.diplomeId)} />
                  </div>
                </div>

                {/* Section 6: Informations Financieres */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Banknote size={16} className="text-gray-400" />
                    Informations Financieres
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem
                      icon={CreditCard}
                      label="RIB"
                      value={profile?.rib ? <span className="font-mono">{profile.rib}</span> : '-'}
                    />
                    <InfoItem
                      icon={Banknote}
                      label="Salaire mensuel net"
                      value={profile?.salaireMensuelNet ? `${profile.salaireMensuelNet} MAD` : '-'}
                    />
                    <InfoItem
                      icon={Building2}
                      label="Campus de paie"
                      value={getPayrollCampusName()}
                    />
                  </div>
                </div>

                {/* Section 7: Contact d'Urgence */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Heart size={16} className="text-gray-400" />
                    Contact d'Urgence
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem icon={User} label="Nom" value={profile?.urgenceNom} />
                    <InfoItem icon={Heart} label="Relation" value={emergencyRelationLabel(profile?.urgenceRelation)} />
                    <InfoItem
                      icon={Phone}
                      label="Telephone"
                      value={formatPhoneDisplay(profile?.urgenceNumero, profile?.urgencePhoneCountry)}
                    />
                    <InfoItem icon={Home} label="Adresse" value={profile?.urgenceAdresse} />
                  </div>
                </div>

                {/* Section 8: Informations Professeur / E-DEP (only for professors) */}
                {isProfessorOrAlso && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-purple-200 bg-purple-50 -mx-6 px-6 py-3 rounded-lg">
                      <GraduationCap size={16} className="text-purple-600" />
                      <span className="text-purple-900">Informations Professeur / E-DEP</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoItem
                        icon={Award}
                        label="Statut"
                        value={profile?.statutProfesseur ? statutProfesseurLabels[profile.statutProfesseur] || profile.statutProfesseur : '-'}
                        iconColor="text-purple-500"
                      />
                      <InfoItem
                        icon={Award}
                        label="Grade"
                        value={getGradeLabel(profile?.gradeId)}
                        iconColor="text-purple-500"
                      />
                      <InfoItem
                        icon={Building2}
                        label="Departement d'attache"
                        value={getDepartmentLabel(profile?.departementAttacheId)}
                        iconColor="text-purple-500"
                      />
                      <InfoItem
                        icon={GraduationCap}
                        label="Diplome"
                        value={getDiplomeLabel(profile?.diplomeId)}
                        iconColor="text-purple-500"
                      />
                      <InfoItem
                        icon={BookOpen}
                        label="Specialite"
                        value={profile?.specialite}
                        iconColor="text-purple-500"
                      />
                      {profile?.statutProfesseur === 'V' && (
                        <>
                          <InfoItem
                            icon={CreditCard}
                            label="Taux horaire"
                            value={profile?.tauxHoraire ? `${profile.tauxHoraire} MAD/h` : '-'}
                            iconColor="text-purple-500"
                          />
                          <InfoItem
                            icon={Building2}
                            label="Etablissement d'origine"
                            value={profile?.etablissementOrigine}
                            iconColor="text-purple-500"
                          />
                        </>
                      )}
                    </div>
                    {/* Matieres enseignees */}
                    <div className="flex items-start gap-3">
                      <BookOpen size={18} className="text-purple-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-2">Matieres enseignees</p>
                        {profile?.matieresEnseignees ? (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">
                              {profile.matieresEnseignees}
                            </p>
                          </div>
                        ) : (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-700">
                              Les matieres enseignees seront generees automatiquement apres l'affectation des cours.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewStaffModalSuperAdmin;
